import defer from 'lodash/defer';
import isNil from 'lodash/isNil';
import {
  Section,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  create,
} from 'zustand';
import {
  RecordingState,
} from 'views/EMR/components/Snap/usePopulateRecord';
import {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';

type SnapMode = 'popoverOnly' | 'withMinimizedControls' | 'closed';

export type VisitDetails = {
  encounterId: number;
  patientId: number;
  patientName: string;
  visitDateTime: string;
};

type SnapState = {
  mode: SnapMode;
  anchorElement: HTMLElement | null;
  snapRecordingState: RecordingState;
  visitDetails: VisitDetails | null;
  generatedNote: Section[] | null;
  inVisitNote: boolean;
  isGeneratingNote: boolean;
  showRedirectMessage: boolean;
  soapNote: Partial<VisitNoteData> | null;
};

type Props = SnapState & {
  actions: {
    closeSnap: () => void;
    openSnap: (anchorElement: HTMLElement, visitDetails: VisitDetails) => void;
    toggleSnapPopover: (anchorElement: HTMLElement | null) => void;
    onNavigateAwayFromVisitNote: () => boolean;
    onNavigateToVisitNote: (encounterId: number) => void;
    setGeneratedNote: (generatedNote: Section[] | null) => void;
    setIsGeneratingNote: (isGeneratingNote: boolean) => void;
    setSnapRecordingState: (snapRecordingState: RecordingState) => void;
    setSoapNote: (soapNote: Partial<VisitNoteData> | null) => void;
  };
};

const initialState: SnapState = {
  mode: 'closed',
  snapRecordingState: 'idle',
  anchorElement: null,
  visitDetails: null,
  generatedNote: null,
  inVisitNote: true,
  isGeneratingNote: false,
  showRedirectMessage: true,
  soapNote: null,
};

const useSnapStore = create<Props>(
  (set, get) => ({
    ...initialState,
    actions: {
      setSnapRecordingState: (snapRecordingState: RecordingState) => {
        set({ snapRecordingState });
      },
      setGeneratedNote: (generatedNote: Section[] | null) => {
        set({ generatedNote });
      },
      onNavigateToVisitNote: (encounterId: number) => {
        const state = get();
        const { visitDetails } = state;

        if (!isNil(visitDetails)) {
          set({ inVisitNote: visitDetails?.encounterId === encounterId });
        } else {
          set({ inVisitNote: false });
        }
      },
      onNavigateAwayFromVisitNote: () => {
        const state = get();
        let showInfo = false;
        const { mode, snapRecordingState, showRedirectMessage } = state;
        const isIdle = snapRecordingState === 'idle';

        if (mode === 'popoverOnly' && !isIdle) {
          if (showRedirectMessage) {
            set({ anchorElement: null });
            set({ showRedirectMessage: false });
            showInfo = true;
          }
          defer(() => { set({ mode: 'withMinimizedControls' }); });
        } else if (mode === 'popoverOnly' && isIdle) {
          set({ mode: 'closed' });
        }
        set({ inVisitNote: false });
        return showInfo;
      },
      closeSnap: () => {
        set({ ...initialState });
      },
      openSnap: (anchorElement: HTMLElement, visitDetails: VisitDetails) => {
        set({
          mode: 'popoverOnly',
          anchorElement,
          visitDetails,
          inVisitNote: true,
          showRedirectMessage: true,
        });
      },
      toggleSnapPopover: (_anchorElement: HTMLElement | null) => {
        const { anchorElement } = get();
        set({
          anchorElement: isNil(anchorElement)
            ? _anchorElement
            : null,
        });
      },
      setIsGeneratingNote: (isGeneratingNote: boolean) => {
        set({ isGeneratingNote });
      },
      setSoapNote: (soapNote: Partial<VisitNoteData> | null) => {
        set({ soapNote });
      },
    },
  }),
);

export const useSnapMode = () => useSnapStore(
  (state: Props) => state.mode,
);

export const useSnapVisitDetails = () => useSnapStore(
  (state: Props) => state.visitDetails,
);

export const useSnapAnchorElement = () => useSnapStore(
  (state: Props) => state.anchorElement,
);

export const useSnapActions = () => useSnapStore(
  (state: Props) => state.actions,
);

export const useSnapGeneratedNote = () => useSnapStore(
  (state: Props) => state.generatedNote,
);

export const useSnapInVisitNote = () => useSnapStore(
  (state: Props) => state.inVisitNote,
);

export const useSnapIsGeneratingNote = () => useSnapStore(
  (state: Props) => state.isGeneratingNote,
);

export const useSnapSoapNote = () => useSnapStore(
  (state: Props) => state.soapNote,
);
